import React, { useContext, useRef, useState } from 'react';

import * as S from './styles';
import { Formik, FormikProps } from 'formik';

import FieldFormik, {
  IFormOptions,
} from '../../../../../components/newStudent/FieldFormik';
import PhoneInput from '../../../../../components/newStudent/PhoneInput';
import { IUserData } from '../..';
import { ButtonSimple } from '../../../../../components/newStudent/Buttons';
import {
  helperServiceNewStudent,
  myDataServiceNewStudent,
} from '../../../../../services/newStudent';
import { toE164 } from '../../../../../util/phone';
import MyDataConfirmModal from '../../../../../components/newStudent/Modal/MyDataConfirmModal';
import { useTheme } from 'styled-components';
import { handleMaxWidth } from '../../../../../util/newStudent';
import { toastMessage } from '../../../../../util/newStudent/toast';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../components/newStudent/ProviderNewStudent';

const CEP_NUMERIC_LENGTH = 8;

interface IFormMyDataProps {
  formValues: IUserData;
  verifiedFields?: IUserData;
  showPolo?: boolean;
  showRaceColor?: boolean;
  optionsPolo?: IFormOptions[];
  showButton?: boolean;
  isDataConference?: boolean;
  nameButton?: string;
  handleButtonClick?: (values: IUserData) => void;
}

export default function FormMyData({
  formValues,
  verifiedFields,
  showPolo,
  showRaceColor,
  isDataConference,
  optionsPolo,
  showButton,
  nameButton,
  handleButtonClick,
}: IFormMyDataProps) {
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newField, setNewField] = useState<{
    email?: string;
    phone?: string | number;
  }>({
    email: '',
    phone: '',
  });

  const [typeConfirmation, setTypeConfirmation] = useState<'email' | 'phone'>(
    'email',
  );
  const lastCepLookupRef = useRef('');

  const handleSendEmail = async (email: string) => {
    setIsLoading(true);
    setNewField({ email: email });
    try {
      const { result } = await myDataServiceNewStudent.generateCode({
        email: email,
      });
      if (result) {
        setIsModalOpen(true);
        setTypeConfirmation('email');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendSms = async (phone: string | number) => {
    setIsLoading(true);
    setNewField({ phone: phone });
    try {
      const { result } = await myDataServiceNewStudent.generateCode({
        phone: phone,
      });
      if (result) {
        setIsModalOpen(true);
        setTypeConfirmation('phone');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const clearAddressFields = (formik: FormikProps<IUserData>) => {
    void formik.setFieldValue('street', '');
    void formik.setFieldValue('neighborhood', '');
    void formik.setFieldValue('city', '');
    void formik.setFieldValue('state', '');
    void formik.setFieldValue('municipalityCode', '');
  };

  const handleCepLookup = async (
    cep: string,
    formik: FormikProps<IUserData>,
  ) => {
    if (!isDataConference || !!verifiedFields?.cep) {
      return;
    }

    const numericCep = String(cep ?? '').replace(/\D/g, '');

    if (numericCep.length === 0) {
      lastCepLookupRef.current = '';
      clearAddressFields(formik);
      return;
    }

    if (numericCep.length < CEP_NUMERIC_LENGTH) {
      return;
    }

    if (lastCepLookupRef.current === numericCep) {
      return;
    }

    lastCepLookupRef.current = numericCep;
    setIsLoading(true);
    try {
      const { data, result, message } =
        await helperServiceNewStudent.consultAddress(numericCep);
      if (result && data) {
        void formik.setFieldValue('street', data.logradouro ?? '');
        void formik.setFieldValue('neighborhood', data.bairro ?? '');
        void formik.setFieldValue('city', data.cidade ?? '');
        void formik.setFieldValue('state', data.uf ?? '');
        void formik.setFieldValue('municipalityCode', data.ibge ?? '');
        return;
      }

      clearAddressFields(formik);
      if (message) {
        toastMessage(message);
      }
    } catch (error) {
      lastCepLookupRef.current = '';
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const theme = useTheme();
  return (
    <S.Container>
      <Formik initialValues={formValues} onSubmit={() => {}} enableReinitialize>
        {(formik) => {
          const { handleChange, values } = formik;

          return (
            <S.StyledForm>
              <FieldFormik
                name="name"
                type="string"
                label="Nome"
                disabled={!isDataConference || !!verifiedFields?.name}
                widthSize="fullWidth"
                onChange={handleChange}
                value={values.name}
              />
              <S.DivLi>
                <FieldFormik
                  name="email"
                  type="email"
                  label="Email"
                  widthSize="fullWidth"
                  onChange={(e) => {
                    handleChange(e);
                    setNewField({
                      email: values.email,
                      phone: '',
                    });
                  }}
                  value={values.email}
                />
                <div>
                  <ButtonSimple
                    color="primary"
                    widthSize={
                      theme.fontSizeType == 'large' ||
                      theme.fontSizeType == 'xLarge'
                        ? 'large'
                        : 'xmedium'
                    }
                    onClick={() => handleSendEmail(values.email)}
                  >
                    Enviar e-mail
                  </ButtonSimple>
                </div>
              </S.DivLi>
              <S.DivLiPhone>
                <PhoneInput
                  className="homePhone"
                  numberName="homePhone"
                  label="Telefone Residencial"
                  disabled={!isDataConference || !!verifiedFields?.homePhone}
                  widthSize="fullWidth"
                  onChange={handleChange}
                  numberValue={values.homePhone}
                />
                <PhoneInput
                  className="commercialPhone"
                  numberName="businessPhone"
                  label="Telefone Comercial"
                  disabled={!isDataConference || !!verifiedFields?.businessPhone}
                  widthSize="fullWidth"
                  onChange={handleChange}
                  numberValue={values.businessPhone}
                />
                <PhoneInput
                  className="cellPhone"
                  numberName="cellPhone"
                  label="Telefone Celular"
                  widthSize="fullWidth"
                  onChange={handleChange}
                  numberValue={values.cellPhone}
                />
                <div>
                  <ButtonSimple
                    className="send_sms"
                    color="primary"
                    widthSize={
                      handleMaxWidth(theme.size?.medium)
                        ? 'fullWidth'
                        : 'xmedium'
                    }
                    onClick={() =>
                      handleSendSms(
                        toE164(undefined, String(values.cellPhone ?? '')),
                      )
                    }
                  >
                    Enviar WhatsApp
                  </ButtonSimple>
                </div>
              </S.DivLiPhone>
              <S.DivLiRgCpf>
                <FieldFormik
                  name="bornDate"
                  type="date"
                  label="Data de Nascimento"
                  disabled={!isDataConference || !!verifiedFields?.bornDate}
                  widthSize="fullWidth"
                  onChange={handleChange}
                  value={values.bornDate}
                />
                <FieldFormik
                  name="rgId"
                  type="string"
                  label="RG"
                  disabled={!isDataConference || !!verifiedFields?.rgId}
                  widthSize="fullWidth"
                  onChange={handleChange}
                  value={values.rgId}
                />
                <FieldFormik
                  name="cpf"
                  type="string"
                  label="CPF"
                  disabled={!isDataConference || !!verifiedFields?.cpf}
                  widthSize="fullWidth"
                  onChange={handleChange}
                  value={values.cpf}
                  mask="999.999.999-99"
                />
              </S.DivLiRgCpf>
              {(showPolo || showRaceColor) && (
                <S.DivLiRgCpf>
                  {showPolo && (
                    <S.DivLiRgCpf>
                      <FieldFormik
                        name="poleId"
                        type="select"
                        label="Polo"
                        options={optionsPolo}
                        widthSize="fullWidth"
                        onChange={handleChange}
                        value={values.poleId}
                      />
                    </S.DivLiRgCpf>
                  )}
                  {showRaceColor && (
                    <FieldFormik
                      name="raceColor"
                      type="select"
                      label="Se declara na cor/raça"
                      disabled={
                        !isDataConference || !!verifiedFields?.raceColor
                      }
                      options={[
                        { title: '[ Selecione ]', value: 0 },
                        { title: 'Branca', value: 1 },
                        { title: 'Preta', value: 2 },
                        { title: 'Amarela', value: 3 },
                        { title: 'Parda', value: 4 },
                        { title: 'Indigena', value: 5 },
                        { title: 'Outra', value: 6 },
                      ]}
                      widthSize="fullWidth"
                      onChange={handleChange}
                      value={values.raceColor}
                    />
                  )}
                </S.DivLiRgCpf>
              )}
              <S.DivAddress>
                <S.DivAddressInfo>
                  <FieldFormik
                    name="cep"
                    type="string"
                    label="CEP"
                    disabled={!isDataConference || !!verifiedFields?.cep}
                    widthSize={theme.size?.medium ? 'fullWidth' : 'medium'}
                    onChange={(e) => {
                      handleChange(e);
                      void handleCepLookup(e.target.value, formik);
                    }}
                    value={values.cep}
                    mask="99999-999"
                  />
                  {isDataConference && (
                    <FieldFormik
                      name="municipalityCode"
                      type="string"
                      label="Código do Município (IBGE)"
                      disabled={
                        !!verifiedFields?.municipalityCode ||
                        !!verifiedFields?.cep
                      }
                      widthSize={theme.size?.medium ? 'fullWidth' : 'medium'}
                      onChange={handleChange}
                      value={values.municipalityCode ?? ''}
                    />
                  )}
                  <FieldFormik
                    name="street"
                    type="string"
                    label="Endereço"
                    disabled={!isDataConference || !!verifiedFields?.street}
                    widthSize="fullWidth"
                    onChange={handleChange}
                    value={values.street}
                  />
                  <FieldFormik
                    name="number"
                    type="string"
                    label="Número"
                    disabled={!isDataConference || !!verifiedFields?.number}
                    widthSize={theme.size?.medium ? 'fullWidth' : 'medium'}
                    onChange={handleChange}
                    value={values.number}
                  />
                  <FieldFormik
                    name="neighborhood"
                    type="string"
                    label="Bairro"
                    disabled={
                      !isDataConference || !!verifiedFields?.neighborhood
                    }
                    widthSize={theme.size?.medium ? 'fullWidth' : 'medium'}
                    onChange={handleChange}
                    value={values.neighborhood}
                  />
                  <FieldFormik
                    name="city"
                    type="string"
                    label="Cidade"
                    disabled={!isDataConference || !!verifiedFields?.city}
                    widthSize={theme.size?.medium ? 'fullWidth' : 'medium'}
                    onChange={handleChange}
                    value={values.city}
                  />
                  <FieldFormik
                    name="state"
                    type="string"
                    label="Estado"
                    disabled={!isDataConference || !!verifiedFields?.state}
                    widthSize={theme.size?.medium ? 'fullWidth' : 'medium'}
                    onChange={handleChange}
                    value={values.state}
                  />
                </S.DivAddressInfo>
              </S.DivAddress>
              <FieldFormik
                name="fatherName"
                type="string"
                label="Nome do Pai"
                disabled={!isDataConference || !!verifiedFields?.fatherName}
                widthSize="fullWidth"
                onChange={handleChange}
                value={values.fatherName}
              />
              <FieldFormik
                name="motherName"
                type="string"
                label="Nome da Mãe"
                disabled={!isDataConference || !!verifiedFields?.motherName}
                widthSize="fullWidth"
                onChange={handleChange}
                value={values.motherName}
              />
              <S.DivGraduation>
                <FieldFormik
                  name="studentGraduation"
                  type="string"
                  label="Curso de Graduação"
                  disabled={
                    !isDataConference || !!verifiedFields?.studentGraduation
                  }
                  widthSize="fullWidth"
                  onChange={handleChange}
                  value={values.studentGraduation}
                />
                <FieldFormik
                  name="university"
                  type="string"
                  label="Instituição"
                  disabled={!isDataConference || !!verifiedFields?.university}
                  widthSize="fullWidth"
                  onChange={handleChange}
                  value={values.university}
                />
                <FieldFormik
                  name="graduationYear"
                  type="string"
                  label="Ano de Graduação"
                  disabled={
                    !isDataConference || !!verifiedFields?.graduationYear
                  }
                  widthSize={theme.size?.medium ? 'fullWidth' : 'large'}
                  onChange={handleChange}
                  value={values.graduationYear}
                />
              </S.DivGraduation>
              {showButton && (
                <S.Button>
                  <ButtonSimple
                    color="primary"
                    widthSize="large"
                    onClick={() =>
                      handleButtonClick && handleButtonClick(values)
                    }
                  >
                    {nameButton}
                  </ButtonSimple>
                </S.Button>
              )}
            </S.StyledForm>
          );
        }}
      </Formik>
      <MyDataConfirmModal
        setIsOpen={setIsModalOpen}
        isOpen={isModalOpen}
        newField={newField}
        type={typeConfirmation}
      />
    </S.Container>
  );
}
